import { Map } from "immutable";
import { combineReducers } from 'redux';

import { UndoableAction } from '../actions';
import { ProcessStep } from "../constants";
import { TemplateData } from "../services/template-service";
import { Locale } from "../services/translation-store";

import { BasicData, Note, Presentation, SlideData, SlidePathData } from './presentation';
import { TemplateOverview, TemplateState } from './template';
import {
    DraggingState,
    GenericDialogState,
    HelpPanelState,
    NoteDialogState,
    PaneState, PanningState,
    SelectorDraggingState,
    ShapeDraggingState,
    SketchMode, SlideNotesDialogState,
    TextShapeDialogState,
    TimeDialogState,
    UIState,
} from './ui-state';
import { UndoHistoryState } from "./undo-history";

import presentationState, * as fromPresentationState from './presentation';
import templateState, * as fromTemplateState from './template';
import uiState, * as fromUIState from './ui-state';
import undoHistoryState, * as fromUndoHistoryState from './undo-history';

export { DraggingState, Note, Presentation, ShapeDraggingState, SelectorDraggingState, SketchMode, SlideData, SlidePathData, TemplateData, TemplateOverview };

export interface State {
    readonly presentationState: Presentation;
    readonly templateState: TemplateState;
    readonly uiState: UIState;
    readonly undoHistoryState: UndoHistoryState;
}


// app-state

// presentation-state
export { ShapeType } from '../services/shape-service';
export const getPresentationId = (state: State): number => fromPresentationState.getPresentationId(state.presentationState);
export const getProcessStep = (state: State): ProcessStep => fromPresentationState.getProcessStep(state.presentationState);
export const getBasicData = (state: State): BasicData => fromPresentationState.getBasicData(state.presentationState);
export const getTemplateId = (state: State): number => fromPresentationState.getTemplateId(state.presentationState);
export const getNotes = (state: State): ReadonlyArray<Note> => fromPresentationState.getNotes(state.presentationState);
export const getSlides = (state: State): ReadonlyArray<SlideData> => fromPresentationState.getSlides(state.presentationState);
export const getTextFields = (state: State): Map<string, string> =>fromPresentationState.getTextFields(state.presentationState);
export const getTextFieldValue = (state: State, textFieldKey: string): string =>
    fromPresentationState.getTextFieldValue(state.presentationState, textFieldKey);

// template-state
export const getTemplateOverviews = (state: State): ReadonlyArray<TemplateOverview> => fromTemplateState.getTemplateOverviews(state.templateState, getLocale(state));
export const getCurrentTemplate = (state: State): TemplateData => fromTemplateState.getCurrentTemplate(state.templateState);
export const getTemplateText = (state: State, id: string): string  =>
    fromTemplateState.getTemplateText(state.templateState, getLocale(state), id);

// ui-state
export { HelpPanelContentType, XSelector, YSelector } from './ui-state';
export const getError = (state: State): Error | null => fromUIState.getError(state.uiState);
export const getLocale = (state: State): Locale => fromUIState.getLocale(state.uiState);
export const getPaneState = (state: State): PaneState => fromUIState.getPaneState(state.uiState);
export const getZoomLevel = (state: State): number => fromUIState.getZoomLevel(state.uiState);
export const getPanning = (state: State): PanningState | null => fromUIState.getPanning(state.uiState);
export const getNoteDragging = (state: State): DraggingState | null => fromUIState.getNoteDragging(state.uiState);
export const getShapeDragging = (state: State): ShapeDraggingState | null => fromUIState.getShapeDragging(state.uiState);
export const getSelectorDragging = (state: State): SelectorDraggingState | null => fromUIState.getSelectorDragging(state.uiState);
export const getSlideDragging = (state: State): DraggingState | null => fromUIState.getSlideDragging(state.uiState);
export const getSketchMode = (state: State): SketchMode => fromUIState.getSketchMode(state.uiState);
export const getSketchState = (state: State): DraggingState | null => fromUIState.getSketchState(state.uiState);
export const getSelectedShape = (state: State): string | null => fromUIState.getSelectedShape(state.uiState);
export const getHelpPanelState = (state: State): HelpPanelState => fromUIState.getHelpPanelState(state.uiState);
export const getGenericDialogState = (state: State): GenericDialogState => fromUIState.getGenericDialogState(state.uiState);
export const getNoteDialogState = (state: State): NoteDialogState => fromUIState.getNoteDialogState(state.uiState);
export const getSlideNotesDialogState = (state:State): SlideNotesDialogState => fromUIState.getSlideNotesDialogState(state.uiState);
export const getTextShapeDialogState = (state:State): TextShapeDialogState => fromUIState.getTextShapeDialogState(state.uiState);
export const getTimeDialogState = (state: State): TimeDialogState => fromUIState.getTimeDialogState(state.uiState);

// undo-history
export const isUndoable = (state: State): boolean => fromUndoHistoryState.isUndoable(state.undoHistoryState);
export const isRedoable = (state: State): boolean => fromUndoHistoryState.isRedoable(state.undoHistoryState);
export const getUndoAction = (state: State): UndoableAction => fromUndoHistoryState.getUndoAction(state.undoHistoryState);
export const getRedoAction = (state: State): UndoableAction => fromUndoHistoryState.getRedoAction(state.undoHistoryState);


export default combineReducers<State>({
    presentationState,
    templateState,
    uiState,
    undoHistoryState,
});



// WEBPACK FOOTER //
// ./src/reducers/index.ts